import { Injectable, OnDestroy, NgZone } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { COOKIE_KEYS, CENTRAL_PLATFORM, STUDY_AND_ROLE_ROUTE } from "../constant/constant";
import { clearLocalStorageKeepingActivity } from "../constant/constant-function";
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { AuthGuardService } from "./auth-guard.service";
import { RoleEnum } from "../models/Role";

export const AUTH_CHANNEL_NAME = 'auth_state_channel';

export const AUTH_EVENTS = {
  ROLE_CHANGED: 'ROLE_CHANGED',
  LOGOUT: 'LOGOUT',
  LOGIN: 'LOGIN'
} as const;

export type AuthEventType = typeof AUTH_EVENTS[keyof typeof AUTH_EVENTS];

const POLL_INTERVAL_MS = 1500;

export type AuthChannelEvent = {
  type: AuthEventType;
};

@Injectable({
  providedIn: 'root'
})
export class AuthBaseService implements OnDestroy {
    private authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private previousRoleId: string = '';

    // Fires when the ROLE cookie transitions from unset to set (e.g. it lands
    // just after login, moments after a component like the header already
    // read it as empty). Components that read roleId on init can resubscribe
    // here to retry instead of being stuck with a stale/blank value.
    private roleAvailableSubject = new Subject<void>();
    readonly roleAvailable$: Observable<void> = this.roleAvailableSubject.asObservable();

    constructor(private cookieService: CookieService, private ngZone: NgZone, 
        private router: Router, private authGuardService: AuthGuardService
    ) { 
        this.previousRoleId  = this.cookieService.get(COOKIE_KEYS.ROLE);
        this.listenForAuthChanges();
        this.startPolling();
    }

    private listenForAuthChanges(): void {
        this.authChannel.onmessage = (event: MessageEvent<AuthChannelEvent>) => {
            const { type } = event.data;
            switch (type) {
            case AUTH_EVENTS.LOGOUT:
                this.ngZone.run(() => {
                    this.authGuardService.logout();
                });
                break;
            case AUTH_EVENTS.ROLE_CHANGED:
                this.ngZone.run(() => {
                    this.router.navigate([''], { replaceUrl: true });
                });
                break;
            case AUTH_EVENTS.LOGIN:
                this.ngZone.run(() => {
                    if (this.router.url === '/' || this.router.url === '/forgotPassword') {
                        this.router.navigate([STUDY_AND_ROLE_ROUTE], { replaceUrl: true });
                    }
                });
                break;
            }
        };
    }


    private startPolling(): void {
        // Outside Angular zone — avoids triggering change detection every tick
        this.ngZone.runOutsideAngular(() => {
            this.pollTimer = setInterval(() => this.checkCookieChanges(), POLL_INTERVAL_MS);
        });
    }


    private checkCookieChanges(): void {
        const currentRoleId  = this.cookieService.get(COOKIE_KEYS.ROLE);

        if (!this.previousRoleId && currentRoleId) {
            this.previousRoleId = currentRoleId;
            this.ngZone.run(() => this.roleAvailableSubject.next());
            return;
        }

        const roleChanged = currentRoleId !== this.previousRoleId;
        if (roleChanged) {
            this.previousRoleId = currentRoleId;
            // a cleared role cookie is a logout, which reloads on its own; reacting here too refreshed the page twice
            if (currentRoleId && this.router.url.includes(CENTRAL_PLATFORM)) {
                this.broadcastAndReload(AUTH_EVENTS.ROLE_CHANGED);
            }
        }
    }

    private broadcastAndReload(type: AuthEventType): void {
        this.authChannel.postMessage({ type } satisfies AuthChannelEvent);
        this.clearStorageData();
        this.ngZone.run(() => {
            this.router.navigate([''], { replaceUrl: true });
        });
    }

    stopPolling(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    broadcastLogout(): void {
        console.log("Broadcasting logout event");
        
        this.authChannel.postMessage({type: AUTH_EVENTS.LOGOUT } satisfies AuthChannelEvent);
        this.authGuardService.logout();
    }

    broadcastLogin(): void {
        this.authChannel.postMessage({ type: AUTH_EVENTS.LOGIN } satisfies AuthChannelEvent);
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.authChannel.close();
    }

    clearStorageData(): void {
        clearLocalStorageKeepingActivity();
        sessionStorage.clear();
    }

    get roleId(): string {
        return this.cookieService.get(COOKIE_KEYS.ROLE);
    }

    isSystemAdmin(): boolean {
        const role = this.roleId;
        return role === RoleEnum.SYSTEM_ADMIN.toString();
    }
}

